import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import GlobalSearchBar from "@/components/GlobalSearchBar";
import HomeBannerStrip from "@/components/HomeBannerStrip";
import api, { fmtApiError } from "@/lib/api";
import { CATEGORIES, formatINR, BRAND, INDIAN_CITIES, DURATION_OPTIONS } from "@/lib/constants";
import { TAXONOMY } from "@/lib/taxonomy";
import EquipmentCard from "@/components/marketplace/EquipmentCard";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowRight, ShieldCheck, Zap, Gavel, FileSearch, Building2, HardHat, MapPin,
  Mail, MessageCircle, Phone, CheckCircle2, Search, Clock, Users, TrendingUp, Sparkles,
  Package, ShoppingCart, Layers, Sun, Wind, Train, Fuel, Factory, Radio, Compass, Wrench,
} from "lucide-react";

const heroImage = "https://images.unsplash.com/photo-1553048686-e3d0396506b9?w=1920&q=80&auto=format&fit=crop";

const stats = [
  { label: "Supplier Network", value: "5,000+", icon: Users },
  { label: "Cities Served", value: "100+", icon: MapPin },
  { label: "Equipment Types", value: "180+", icon: Layers },
  { label: "Coverage", value: "Pan India", icon: ShieldCheck },
];

const sectors = [
  { name: "Power Transmission", icon: Zap },
  { name: "Substations", icon: Zap },
  { name: "Infrastructure", icon: Building2 },
  { name: "Renewable Energy", icon: Sun },
  { name: "Railways", icon: Compass },
  { name: "Metro Projects", icon: Train },
  { name: "Industrial Plants", icon: Factory },
  { name: "Oil & Gas", icon: Fuel },
  { name: "Manufacturing", icon: Wrench },
  { name: "Marine Projects", icon: Wind },
];

const whyFeatures = [
  { t: "Single Point Coordination", d: "One team handles supplier discovery, quotes and negotiation.", icon: MessageCircle },
  { t: "Pan India Supplier Access", d: "5,000+ verified suppliers across 100+ cities.", icon: MapPin },
  { t: "Multiple Quote Comparison", d: "Compare 3+ quotes side-by-side before deciding.", icon: TrendingUp },
  { t: "Inspection & Certification", d: "TPI, load testing, certification via verified partners.", icon: ShieldCheck },
  { t: "Lead Protection", d: "Your enquiry stays private. No spam calls from strangers.", icon: ShieldCheck },
  { t: "Vendor Verification", d: "KYC-checked partners with GST + docs on file.", icon: Users },
  { t: "EquipmentHub Managed Deals", d: "Every transaction supported by our team end-to-end.", icon: HardHat },
  { t: "Founder Control", d: "Critical negotiations monitored through EHI workflows.", icon: ShoppingCart },
];

const specializedCategories = [
  { title: "Transmission Line Equipment", items: ["TL Winch 5T", "TL Puller", "TL Tensioner", "TL Compressor", "Hydraulic Winch"], icon: Zap },
  { title: "Piling Equipment", items: ["Piling Rig", "Vibro Hammer", "Desander", "Sheet Pile Driver"], icon: Building2 },
  { title: "Material Handling", items: ["Builders Hoist", "Rack & Pinion Hoist", "Tower Crane", "Forklift"], icon: HardHat },
  { title: "Railway Equipment", items: ["RRV", "Rail Threader", "Tamper"], icon: Train },
  { title: "Marine Equipment", items: ["Crane Barges", "Jack Up Barges"], icon: Wind },
];

const topBrands = ["ACE", "JCB", "Tata Hitachi", "SANY", "Manitou", "Ajax", "Caterpillar", "Schwing Stetter"];

const trustBadges = [
  { label: "Verified Enquiries", icon: ShieldCheck },
  { label: "Verified Suppliers", icon: Users },
  { label: "EHI-Managed Communication", icon: MessageCircle },
  { label: "Quote Comparison System", icon: TrendingUp },
  { label: "Lead Protection Enabled", icon: ShieldCheck },
];

function HeroSearch() {
  const nav = useNavigate();
  const [mode, setMode] = useState("precise");   // precise | ai
  const [f, setF] = useState({ category: "", capacity: "", city: "", duration: "" });
  const [aiQuery, setAiQuery] = useState("");
  const upd = (k, v) => setF({ ...f, [k]: v });

  const submitPrecise = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (f.category) p.set("category", f.category);
    if (f.capacity) p.set("q", f.capacity);
    if (f.city) p.set("city", f.city);
    if (f.duration) p.set("duration", f.duration);
    nav(`/marketplace?${p.toString()}`);
  };

  const submitAi = (e, seed) => {
    e?.preventDefault?.();
    const q = (seed || aiQuery || "").trim();
    if (q.length < 2) return;
    nav(`/search?q=${encodeURIComponent(q)}`);
  };

  const AI_EXAMPLES = [
    "Need 50T crane in Mumbai",
    "Excavator for 3 months in Delhi",
    "Equipment transportation Chennai → Pune",
    "Piling rig on rent · Ahmedabad",
    "DG set 250 kVA next week",
  ];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_28px_60px_-24px_rgba(11,31,58,0.55)] overflow-hidden" data-testid="hero-search-card">
      {/* Segmented tab header */}
      <div className="flex items-stretch border-b border-slate-100 bg-slate-50">
        <button
          type="button"
          onClick={() => setMode("precise")}
          className={`flex-1 flex items-center justify-center gap-2 px-5 py-3.5 text-[13px] font-bold uppercase tracking-[0.12em] transition-all ${
            mode === "precise"
              ? "bg-white text-ehi-primary shadow-[inset_0_-2px_0_var(--ehi-accent,#F57C00)]"
              : "text-slate-500 hover:text-ehi-primary"
          }`}
          data-testid="hero-tab-precise"
        >
          <Search className="w-4 h-4" /> Precise Search
        </button>
        <button
          type="button"
          onClick={() => setMode("ai")}
          className={`flex-1 flex items-center justify-center gap-2 px-5 py-3.5 text-[13px] font-bold uppercase tracking-[0.12em] transition-all ${
            mode === "ai"
              ? "bg-white text-ehi-primary shadow-[inset_0_-2px_0_var(--ehi-accent,#F57C00)]"
              : "text-slate-500 hover:text-ehi-primary"
          }`}
          data-testid="hero-tab-ai"
        >
          <Sparkles className="w-4 h-4" /> Ask AI
        </button>
      </div>

      {/* Panel */}
      {mode === "precise" ? (
        <form onSubmit={submitPrecise} className="p-3 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2" data-testid="hero-search-form">
          <Select value={f.category} onValueChange={(v) => upd("category", v)}>
            <SelectTrigger className="h-12 border-0 shadow-none focus:ring-0 text-sm md:border-r border-slate-200 rounded-none px-3" data-testid="hero-select-category">
              <Package className="w-4 h-4 text-slate-400 mr-2" />
              <SelectValue placeholder="Equipment" />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              {CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 px-3 md:border-r border-slate-200">
            <HardHat className="w-4 h-4 text-slate-400 shrink-0" />
            <input value={f.capacity} onChange={(e) => upd("capacity", e.target.value)} placeholder="Capacity" className="w-full outline-none text-sm py-3 bg-transparent" data-testid="hero-input-capacity" />
          </div>
          <Select value={f.city} onValueChange={(v) => upd("city", v)}>
            <SelectTrigger className="h-12 border-0 shadow-none focus:ring-0 text-sm md:border-r border-slate-200 rounded-none px-3" data-testid="hero-select-city">
              <MapPin className="w-4 h-4 text-slate-400 mr-2" />
              <SelectValue placeholder="Location" />
            </SelectTrigger>
            <SelectContent>{INDIAN_CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={f.duration} onValueChange={(v) => upd("duration", v)}>
            <SelectTrigger className="h-12 border-0 shadow-none focus:ring-0 text-sm px-3" data-testid="hero-select-duration">
              <Clock className="w-4 h-4 text-slate-400 mr-2" />
              <SelectValue placeholder="Duration" />
            </SelectTrigger>
            <SelectContent>{DURATION_OPTIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
          <button type="submit" className="h-12 px-6 rounded-full bg-ehi-accent hover:bg-orange-600 text-white font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2 shadow-[0_10px_24px_-8px_rgba(249,115,22,0.6)]" data-testid="hero-search-submit">
            <Search className="w-4 h-4" /> Search
          </button>
        </form>
      ) : (
        <form onSubmit={submitAi} className="p-3" data-testid="hero-ai-form">
          <div className="flex items-center gap-2 px-3 border border-slate-200 rounded-xl focus-within:border-ehi-primary focus-within:ring-2 focus-within:ring-ehi-primary/15 transition-all">
            <Sparkles className="w-5 h-5 text-ehi-accent shrink-0" />
            <input
              value={aiQuery}
              onChange={(e) => setAiQuery(e.target.value)}
              placeholder="Need 50T crane in Mumbai · Excavator for 3 months · Equipment transportation…"
              className="flex-1 py-3 bg-transparent outline-none text-sm"
              data-testid="hero-ai-input"
              autoFocus
            />
            <button type="submit" className="my-1.5 h-9 px-5 rounded-full bg-ehi-accent hover:bg-orange-600 text-white text-sm font-bold uppercase tracking-wider inline-flex items-center gap-1.5" data-testid="hero-ai-submit">
              Ask <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="mt-3 px-1 flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 mr-1">Try</span>
            {AI_EXAMPLES.map((ex) => (
              <button
                key={ex}
                type="button"
                onClick={(e) => submitAi(e, ex)}
                className="text-xs px-3 py-1 rounded-full border border-slate-200 text-slate-600 hover:border-ehi-primary hover:text-ehi-primary hover:bg-ehi-primary/5 transition-colors"
                data-testid={`hero-ai-ex-${ex.slice(0, 12).replace(/[^a-z]/gi, "-").toLowerCase()}`}
              >
                {ex}
              </button>
            ))}
          </div>
        </form>
      )}
    </div>
  );
}

function AssistantSection() {
  const nav = useNavigate();
  const [f, setF] = useState({ load: "", height: "", radius: "", city: "", duration: "" });
  const [recs, setRecs] = useState(null);
  const upd = (k, v) => setF({ ...f, [k]: v });

  const suggest = (e) => {
    e.preventDefault();
    const load = parseFloat(f.load) || 0;
    const height = parseFloat(f.height) || 0;
    const radius = parseFloat(f.radius) || 0;
    const list = [];
    // Simple rule engine
    if (load > 0 && (height > 30 || radius > 20)) list.push({ id: "all-terrain-crane", name: "All Terrain Crane", reason: `Best for ${load}T at ${Math.max(height, radius)}m reach.` });
    if (load > 0 && height <= 30 && radius <= 20) list.push({ id: "mobile-crane", name: "Mobile Crane", reason: `Standard lift up to ${load}T.` });
    if (load > 50) list.push({ id: "crawler-crane", name: "Crawler Crane", reason: `Heavy lifts above 50T — stable on ground.` });
    if (height > 15 && load <= 5) list.push({ id: "boom-lift", name: "Boom Lift", reason: `Personnel access at ${height}m elevation.` });
    if (height > 8 && height <= 20 && load <= 3) list.push({ id: "scissor-lift", name: "Scissor Lift", reason: `Vertical access up to ${height}m.` });
    if (load > 0 && load <= 5 && height <= 5) list.push({ id: "forklift", name: "Forklift", reason: `Warehouse/site material handling.` });
    if (list.length === 0) list.push({ id: "telehandler", name: "Telehandler", reason: "Versatile choice for light/medium jobs." });
    setRecs(list.slice(0, 3));
  };

  return (
    <section className="max-w-[1700px] mx-auto px-4 lg:px-8 py-10 sm:py-16 lg:py-12 sm:py-16 lg:py-20" data-testid="assistant-section">
      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10">
        <div>
          <div className="label-eyebrow mb-2 text-ehi-accent">Equipment Selection Assistant</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight leading-tight">
            Not sure which equipment fits your <span className="text-ehi-primary">project scope?</span>
          </h2>
          <p className="mt-3 text-slate-600">Tell us the load, height, radius, location and duration — we'll recommend suitable equipment types instantly.</p>
          <div className="mt-4 inline-flex items-center gap-2 bg-ehi-primary/5 border border-ehi-primary/20 rounded-md px-3 py-2">
            <Sparkles className="w-4 h-4 text-ehi-accent" />
            <span className="text-sm text-slate-700">
              Need a <strong>detailed crane recommendation</strong>?{" "}
              <a href="/tools/crane-selector" className="text-ehi-primary font-semibold hover:underline" data-testid="assistant-crane-tool-link">
                Try the Crane Selection Assistant →
              </a>
            </span>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3">
            {[
              { l: "Load Capacity", d: "In tons or kg" },
              { l: "Height Required", d: "Vertical reach in meters" },
              { l: "Radius / Distance", d: "Horizontal reach" },
              { l: "Location & Duration", d: "Where & how long" },
            ].map((x) => (
              <div key={x.l} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                <div className="text-sm font-semibold text-slate-900">{x.l}</div>
                <div className="text-xs text-slate-500 mt-1">{x.d}</div>
              </div>
            ))}
          </div>
        </div>
        <form onSubmit={suggest} className="ehi-card p-6 md:p-8" data-testid="assistant-form">
          <h3 className="font-display font-bold text-lg text-slate-900 mb-4">Get instant recommendations</h3>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label-eyebrow block mb-2">Load (T)</label><Input type="number" value={f.load} onChange={(e) => upd("load", e.target.value)} data-testid="assistant-load" placeholder="e.g. 25" /></div>
            <div><label className="label-eyebrow block mb-2">Height (m)</label><Input type="number" value={f.height} onChange={(e) => upd("height", e.target.value)} data-testid="assistant-height" placeholder="e.g. 20" /></div>
            <div><label className="label-eyebrow block mb-2">Radius (m)</label><Input type="number" value={f.radius} onChange={(e) => upd("radius", e.target.value)} data-testid="assistant-radius" placeholder="e.g. 15" /></div>
            <div><label className="label-eyebrow block mb-2">Location</label>
              <Select value={f.city} onValueChange={(v) => upd("city", v)}>
                <SelectTrigger data-testid="assistant-city"><SelectValue placeholder="City" /></SelectTrigger>
                <SelectContent>{INDIAN_CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><label className="label-eyebrow block mb-2">Duration</label>
              <Select value={f.duration} onValueChange={(v) => upd("duration", v)}>
                <SelectTrigger data-testid="assistant-duration"><SelectValue placeholder="Rental duration" /></SelectTrigger>
                <SelectContent>{DURATION_OPTIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <button className="btn-accent w-full mt-5 inline-flex items-center justify-center gap-2" data-testid="assistant-submit">
            <Sparkles className="w-4 h-4" /> Get recommendations
          </button>
          {recs && (
            <div className="mt-6 border-t border-slate-200 pt-5" data-testid="assistant-recs">
              <div className="label-eyebrow mb-3">Recommended for you</div>
              <ul className="space-y-3">
                {recs.map((r) => (
                  <li key={r.id} className="flex items-start gap-3 p-3 border border-slate-200 rounded-md hover:border-ehi-primary transition-colors">
                    <div className="w-8 h-8 rounded bg-ehi-primary/10 text-ehi-primary grid place-items-center shrink-0"><CheckCircle2 className="w-4 h-4" /></div>
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">{r.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{r.reason}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => nav(`/marketplace?category=${r.id}${f.city ? `&city=${f.city}` : ""}`)}
                      className="text-xs font-semibold text-ehi-accent hover:underline shrink-0"
                    >Browse →</button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}

function HomeContactMiniForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { ...form, subject: "Homepage enquiry — general" });
      setSent(true);
      toast.success("Message sent to " + BRAND.contactEmail);
    } catch (err) {
      toast.error(fmtApiError(err.response?.data?.detail) || "Failed to send");
    } finally { setBusy(false); }
  };
  if (sent) return (
    <div className="mt-6 text-center py-6">
      <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 grid place-items-center mb-3"><CheckCircle2 className="w-6 h-6 text-emerald-600" /></div>
      <div className="font-semibold text-slate-900">Message received</div>
      <div className="text-sm text-slate-600 mt-1">Our team will respond within one business day.</div>
    </div>
  );
  return (
    <form onSubmit={submit} className="mt-5 space-y-3" data-testid="home-contact-mini-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <Input required placeholder="Your name" value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="home-contact-name" />
        <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="home-contact-email-input" />
      </div>
      <Input placeholder="Phone (optional)" value={form.phone} onChange={(e) => upd("phone", e.target.value)} />
      <Textarea rows={3} required placeholder="How can we help?" value={form.message} onChange={(e) => upd("message", e.target.value)} data-testid="home-contact-message" />
      <button disabled={busy} className="btn-accent w-full disabled:opacity-50" data-testid="home-contact-submit">{busy ? "Sending…" : "Send message"}</button>
    </form>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/equipment", { params: { featured: true, limit: 8 } }).then((r) => setFeatured(r.data.items));
  }, []);

  return (
    <div>
      <HomeBannerStrip />
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0">
          <img src={heroImage} alt="" className="w-full h-full object-cover" />
          {/* Layered navy tint for depth (no muddy gradient) */}
          <div className="absolute inset-0 bg-[#050B14]/78" />
          <div className="absolute inset-y-0 left-0 w-2/3 bg-gradient-to-r from-[#050B14]/95 via-[#050B14]/60 to-transparent" />
        </div>
        <div className="ds-grain absolute inset-0" />
        {/* Saffron kinetic hairline at hero bottom edge */}
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-ehi-accent to-transparent z-10" aria-hidden />

        <div className="relative ds-page pt-16 pb-32 lg:pt-24 lg:pb-40">
          <div className="max-w-3xl">
            {/* Live status kicker — factual, not decorative duplicate of H1 */}
            <div
              className="inline-flex items-center gap-2.5 bg-white/8 backdrop-blur border border-white/20 rounded-sm px-3 py-1.5 text-white text-[11px] tracking-[0.2em] uppercase font-bold mb-7"
              data-testid="hero-kicker"
              style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-ehi-accent opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-ehi-accent" />
              </span>
              Live · 5,000+ Suppliers · 100+ Cities
            </div>

            <h1
              className="font-display font-black text-white text-3xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] sm:leading-[0.98] tracking-[-0.03em] sm:tracking-[-0.035em]"
              data-testid="hero-headline"
            >
              India&apos;s <span className="text-ehi-accent">Integrated Infrastructure</span>
              <br />Marketplace.
            </h1>

            <p className="mt-5 sm:mt-6 text-sm sm:text-lg text-slate-200/90 max-w-2xl leading-relaxed">
              Rent, buy, sell and source heavy equipment · industrial materials · industrial scrap &amp; asset disposal · skilled workforce · inspection services<span className="hidden sm:inline"> — plus <b className="text-white">Finance</b>, <b className="text-white">Insurance</b> &amp; <b className="text-white">Logistics</b> hubs, <b className="text-white">AMC / FMC contracts</b>, <b className="text-white">24×7 breakdown response</b>, <b className="text-white">Consulting</b>, <b className="text-white">Fleet + Maintenance Cloud SaaS</b>, free <b className="text-white">Equipment Intelligence</b> and a nationwide network of verified suppliers, buyers, partners and service providers — with lead protection, KYC-verified counterparties and EHI-managed communication on every deal</span>.
            </p>

            {/* Contact strip removed — moved to floating widget + footer */}

            <div className="mt-6 sm:mt-9 grid grid-cols-1 sm:flex sm:flex-wrap sm:items-center gap-3">
              {/* Primary CTA — Post Requirement */}
              <Link
                to="/post-requirement"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-ehi-accent hover:bg-orange-600 text-white font-black uppercase tracking-wider text-sm px-6 py-3 min-h-[48px] shadow-[0_12px_32px_-8px_rgba(249,115,22,0.6)] ring-1 ring-orange-600/40 transition-colors"
                data-testid="hero-cta-post-req"
              >
                Post Requirement <ArrowRight className="w-4 h-4" />
              </Link>
              {/* Secondary CTA — Browse Equipment */}
              <Link
                to="/marketplace"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white/12 hover:bg-white/20 backdrop-blur border border-white/40 text-white font-bold uppercase tracking-wider text-sm px-5 py-3 min-h-[48px] transition-colors"
                data-testid="hero-cta-browse"
              >
                Browse Equipment <ArrowRight className="w-4 h-4" />
              </Link>
              {/* Tertiary — small text links on their own row for mobile */}
              <div className="flex flex-wrap gap-x-4 gap-y-1 col-span-full sm:col-auto sm:ml-1 mt-1 sm:mt-0">
                <Link
                  to="/become-supplier"
                  className="text-white/70 hover:text-white text-xs sm:text-sm font-semibold underline-offset-4 hover:underline transition-colors"
                  data-testid="hero-cta-register-supplier"
                >
                  Register as supplier →
                </Link>
                <Link
                  to="/become-scrap-buyer"
                  className="text-white/70 hover:text-white text-xs sm:text-sm font-semibold underline-offset-4 hover:underline transition-colors"
                  data-testid="hero-cta-register-scrap-buyer"
                >
                  Become a scrap buyer →
                </Link>
              </div>
            </div>
          </div>
          <div className="mt-12 max-w-5xl">
            <HeroSearch />
          </div>
        </div>

        {/* Trust badges strip */}
        <div className="ds-page py-3 pb-6">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 items-center" data-testid="home-trust-badges">
            {trustBadges.map((b) => (
              <div key={b.label} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                <b.icon className="w-3.5 h-3.5 text-emerald-700" strokeWidth={2.2} /> {b.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPECIALIZED EQUIPMENT ADVANTAGE */}
      <section className="max-w-[1700px] mx-auto px-4 lg:px-8 py-10 sm:py-14 lg:py-16" data-testid="home-specialized">
        <div className="text-center mb-10">
          <div className="label-eyebrow mb-2 text-ehi-accent">Specialist Marketplace</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900">Specialized equipment other marketplaces don't offer</h2>
          <p className="mt-2 text-slate-600 max-w-2xl mx-auto">Transmission line, piling, railway, marine and heavy material-handling equipment — sourced from EPC-experienced suppliers, not generalists.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {specializedCategories.map((c) => (
            <div key={c.title} className="ehi-card p-5">
              <div className="w-10 h-10 rounded-md bg-ehi-primary/10 text-ehi-primary grid place-items-center mb-3"><c.icon className="w-5 h-5" /></div>
              <h3 className="font-display font-bold text-lg text-slate-900">{c.title}</h3>
              <ul className="mt-2 text-sm text-slate-600 space-y-1">
                {c.items.map((i) => <li key={i} className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />{i}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* INDUSTRIES SERVED */}
      <section className="bg-slate-100 py-10 sm:py-14 lg:py-16" data-testid="home-industries">
        <div className="max-w-[1700px] mx-auto px-4 lg:px-8">
          <div className="text-center mb-10">
            <div className="label-eyebrow mb-2 text-ehi-accent">Built for Enterprise Projects</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900">Built for Infrastructure, Transmission, Industrial and Construction Projects</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {sectors.map((s) => (
              <div key={s.name} className="ehi-card p-4 flex items-center gap-2">
                <div className="w-8 h-8 rounded bg-ehi-primary/10 text-ehi-primary grid place-items-center shrink-0"><s.icon className="w-4 h-4" /></div>
                <span className="text-sm font-semibold text-slate-800">{s.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TOP BRANDS + BUY NEW CTA */}
      <section className="max-w-[1700px] mx-auto px-4 lg:px-8 py-10 sm:py-14 lg:py-16" data-testid="home-brands">
        <div className="ehi-card p-5 sm:p-8 text-white overflow-hidden relative" style={{ background: "linear-gradient(135deg, #0a3d7a 0%, #0a3d7a 60%, #072e5c 100%)" }}>
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8 items-center">
            <div>
              <div className="label-eyebrow mb-2 text-ehi-accent">Buy New Equipment</div>
              <h2 className="font-display font-black text-3xl sm:text-4xl">Brand-new equipment from OEM-authorised dealers</h2>
              <p className="mt-3 text-slate-200 max-w-lg">Get the best OEM pricing on 8+ trusted brands, coordinated by EquipmentHub India. One enquiry, three quotes, one decision.</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/buy-new" className="btn-accent inline-flex items-center gap-2">Browse New Equipment <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/post-requirement" className="border-2 border-white/40 text-white font-semibold px-5 py-2.5 rounded-md hover:bg-white/10 inline-flex items-center gap-2">Request Best Offer</Link>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              {topBrands.map((b) => (
                <div key={b} className="bg-white/95 text-ehi-primary rounded-lg px-4 py-3 font-display font-black text-sm shadow-md">{b}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1700px] mx-auto px-4 lg:px-8 py-12 sm:py-16 lg:py-12 sm:py-16 lg:py-20">
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow mb-2">Browse by Category</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900">A complete heavy equipment marketplace</h2>
            <p className="mt-2 text-slate-600">22 major categories · 180+ equipment types · Rent · Buy · Inspect.</p>
          </div>
          <Link to="/categories" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-ehi-primary hover:text-ehi-accent transition-colors">View all categories <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {TAXONOMY.slice(0, 8).map((g) => (
            <Link key={g.id} to={`/category/${g.id}`} className="ehi-card overflow-hidden group flex flex-col" data-testid={`home-group-${g.id}`}>
              <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
                <img src={g.image} alt={g.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="text-white/80 text-[10px] tracking-widest uppercase font-semibold mb-1">{g.subcategories.length} types</div>
                  <div className="font-display font-black text-white text-lg leading-tight">{g.name}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          {TAXONOMY.slice(8).map((g) => (
            <Link key={g.id} to={`/category/${g.id}`} className="text-xs px-3 py-2 rounded-full border border-slate-200 bg-white hover:border-ehi-primary hover:text-ehi-primary font-semibold text-slate-700 transition-colors">
              {g.name} <span className="text-slate-400">· {g.subcategories.length}</span>
            </Link>
          ))}
          <Link to="/categories" className="text-xs px-3 py-2 rounded-full bg-ehi-primary text-white font-semibold hover:bg-ehi-primary/90 transition-colors inline-flex items-center gap-1">
            View all {TAXONOMY.length} categories <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </section>

      {/* INSPECTION & CERTIFICATION */}
      <section className="bg-gradient-to-br from-slate-950 to-slate-900 text-white py-12 sm:py-16 lg:py-20 relative overflow-hidden">
        <div className="hero-grain absolute inset-0 opacity-40" />
        <div className="relative max-w-[1700px] mx-auto px-4 lg:px-8 grid md:grid-cols-[1.2fr_1fr] gap-10 items-center">
          <div>
            <div className="label-eyebrow text-ehi-accent mb-2">EquipmentHub · Inspection & Certification</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              Verified Inspection<br />
              <span className="text-ehi-accent">Testing & Certification</span>
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed max-w-xl">
              TPI, load testing, equipment certification, electrical testing, safety compliance — matched with NABL-accredited partners and coordinated by EquipmentHub India end to end.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/inspection/request" className="btn-accent inline-flex items-center gap-2" data-testid="home-ins-request">Request Inspection <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/inspection/partner-register" className="border border-white/40 text-white hover:bg-white/10 transition-colors px-5 py-2.5 rounded-md font-semibold" data-testid="home-ins-partner">Become Inspection Partner</Link>
            </div>
          </div>
          <div className="ehi-card bg-white/95 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-md bg-emerald-100 text-emerald-700 grid place-items-center"><ShieldCheck className="w-5 h-5" /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Featured services</div>
                <div className="font-semibold text-slate-900">TPI · Load Testing · Certification</div>
              </div>
            </div>
            <ul className="divide-y divide-slate-100">
              {[
                "Third Party Inspection (TPI)", "Load Testing", "Crane Inspection",
                "Electrical / Substation Testing", "Safety Compliance Audit",
              ].map((s) => (
                <li key={s} className="py-2.5 text-sm text-slate-700 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {s}
                </li>
              ))}
            </ul>
            <div className="mt-4 text-xs text-slate-500">Inspection coordination managed through EquipmentHub India.</div>
          </div>
        </div>
      </section>

      {/* BUY SELL RENT */}
      <section className="max-w-[1700px] mx-auto px-4 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="label-eyebrow mb-2 text-ehi-accent">Buy • Sell • Rent Marketplace</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight">
            Buy, Sell &amp; Rent Heavy Equipment <span className="text-ehi-primary">with Ease</span>
          </h2>
          <p className="mt-3 text-slate-600">A single platform for equipment rentals, equipment trading, used equipment sales and asset utilization.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { title: "Buy Equipment", desc: "Compare used equipment from verified sellers across India.", cta: "Buy Equipment", to: "/buy-equipment", icon: ShoppingCart },
            { title: "Sell Equipment", desc: "List your idle asset — reach thousands of active buyers.", cta: "Sell Equipment", to: "/sell-equipment", icon: Package },
            { title: "Rent Equipment", desc: "Book cranes, lifts, excavators, gensets — rentals from ₹2,400/day.", cta: "Rent Equipment", to: "/marketplace?type=rental", icon: Wrench },
          ].map((c) => (
            <div key={c.title} className="ehi-card p-6 flex flex-col group" data-testid={`bsr-card-${c.title.split(" ")[0].toLowerCase()}`}>
              <div className="w-11 h-11 rounded-md bg-ehi-primary/10 text-ehi-primary grid place-items-center mb-4 group-hover:bg-ehi-primary group-hover:text-white transition-colors"><c.icon className="w-5 h-5" /></div>
              <h3 className="font-display font-bold text-xl text-slate-900">{c.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{c.desc}</p>
              <Link to={c.to} className="btn-accent mt-5 inline-flex items-center justify-center gap-2 self-start" data-testid={`bsr-cta-${c.title.split(" ")[0].toLowerCase()}`}>
                {c.cta} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white border-y border-slate-200 py-12 sm:py-16 lg:py-20">
        <div className="max-w-[1700px] mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="label-eyebrow mb-2">How EquipmentHub Works</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight">4 simple steps to close a deal</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-5 relative">
            {[
              { n: "01", t: "Post Requirement", d: "Specify equipment, capacity, location & duration in 60 seconds.", icon: FileSearch },
              { n: "02", t: "Receive Supplier Quotes", d: "Verified vendors respond with tailored quotes.", icon: MessageCircle },
              { n: "03", t: "Compare & Negotiate", d: "Compare pricing and availability side-by-side.", icon: Search },
              { n: "04", t: "Close Deal", d: "Award the contract and track completion via CRM.", icon: CheckCircle2 },
            ].map((s, i) => (
              <div key={s.n} className="relative">
                <div className="ehi-card p-6 h-full">
                  <div className="flex items-center justify-between mb-4">
                    <div className="font-display font-black text-4xl text-ehi-accent leading-none font-mono-tab">{s.n}</div>
                    <div className="w-10 h-10 rounded-md bg-ehi-primary text-white grid place-items-center"><s.icon className="w-5 h-5" /></div>
                  </div>
                  <h3 className="font-display font-bold text-lg text-slate-900">{s.t}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
                </div>
                {i < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 -translate-y-1/2 z-10">
                    <div className="w-6 h-6 rounded-full bg-white border-2 border-ehi-primary text-ehi-primary grid place-items-center"><ArrowRight className="w-3 h-3" /></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY EQUIPMENTHUB */}
      <section className="max-w-[1700px] mx-auto px-4 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="label-eyebrow mb-2 text-ehi-accent">Why Industry Chooses EquipmentHub India</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight">Built for scale. Designed for enterprise.</h2>
          <p className="mt-3 text-slate-600">Every transaction is supported by EquipmentHub India. Critical negotiations and supplier coordination are monitored through EHI workflows.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {whyFeatures.map((f) => (
            <div key={f.t} className="ehi-card p-5">
              <div className="w-10 h-10 rounded-md bg-ehi-accent/10 text-ehi-accent grid place-items-center mb-3"><f.icon className="w-5 h-5" /></div>
              <div className="font-display font-bold text-slate-900 leading-snug">{f.t}</div>
              <div className="text-sm text-slate-600 mt-1 leading-relaxed">{f.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* EQUIPMENT SELECTION ASSISTANT */}
      <div className="bg-slate-50 border-y border-slate-200">
        <AssistantSection />
      </div>

      {/* TRUSTED SECTORS */}
      <section className="max-w-[1700px] mx-auto px-4 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="label-eyebrow mb-2">Trusted for Projects Across India</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight">Powering India&apos;s infrastructure ambition</h2>
          <p className="mt-3 text-slate-600">From metro rail to solar parks — EquipmentHub India serves every sector shaping the country.</p>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3">
          {sectors.map((s) => (
            <div key={s.name} className="ehi-card p-4 flex flex-col items-center text-center hover:border-ehi-accent transition-colors" data-testid={`sector-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="w-10 h-10 rounded-md bg-ehi-primary/10 text-ehi-primary grid place-items-center mb-2"><s.icon className="w-5 h-5" /></div>
              <div className="text-xs font-semibold text-slate-700 leading-tight">{s.name}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="bg-white border-y border-slate-200 py-12 sm:py-16 lg:py-20">
          <div className="max-w-[1700px] mx-auto px-4 lg:px-8">
            <div className="flex items-end justify-between mb-8 gap-4">
              <div>
                <div className="label-eyebrow mb-2 text-ehi-accent inline-flex items-center gap-2">Featured Listings <span className="text-[10px] uppercase tracking-widest font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Illustrative Examples</span></div>
                <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900">Popular rentals right now</h2>
                <p className="text-xs text-slate-500 mt-1">Sample listings shown for illustration. Real pricing & availability confirmed after enquiry.</p>
              </div>
              <Link to="/marketplace" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-ehi-primary hover:text-ehi-accent transition-colors">Browse all <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {featured.map((it) => <EquipmentCard key={it.id} item={it} />)}
            </div>
          </div>
        </section>
      )}

      {/* SUPPLIER CTA */}
      <section className="max-w-[1700px] mx-auto px-4 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="ehi-card p-8 md:p-12 grid md:grid-cols-[1.4fr_1fr] gap-8 items-center relative overflow-hidden">
          <div className="absolute -right-20 -top-16 w-72 h-72 rounded-full bg-ehi-accent/10 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="label-eyebrow mb-2 text-ehi-accent">Supplier Registration</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 leading-tight">
              Own Equipment? <span className="text-ehi-primary">Join EquipmentHub India.</span>
            </h2>
            <p className="mt-3 text-slate-600 max-w-lg leading-relaxed">
              Connect with customers across India, get high-intent leads directly to your dashboard, and grow
              your rental & sale revenue with a modern CRM built for heavy equipment.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/register?role=vendor" className="btn-accent inline-flex items-center gap-2" data-testid="supplier-cta-register"><ArrowRight className="w-4 h-4" /> Register Supplier</Link>
              <Link to="/become-supplier" className="btn-outline-primary" data-testid="supplier-cta-list">List Equipment</Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 relative">
            {[
              { k: "Pan India", v: "Network coverage" },
              { k: "0%", v: "Platform commission" },
              { k: "24 hr", v: "Avg. response time" },
              { k: "₹0", v: "Setup fee (Free tier)" },
            ].map((s) => (
              <div key={s.v} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                <div className="font-display font-black text-2xl text-ehi-primary font-mono-tab">{s.k}</div>
                <div className="text-xs text-slate-600 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT US */}
      <section id="contact" className="bg-white border-y border-slate-200 py-12 sm:py-16 lg:py-20">
        <div className="max-w-[1700px] mx-auto px-4 lg:px-8 grid md:grid-cols-[1fr_1.1fr] gap-10 items-start">
          <div>
            <div className="label-eyebrow mb-2 text-ehi-accent">Contact Us</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 leading-tight">
              Talk to our team — <span className="text-ehi-primary">we respond within 24 hours.</span>
            </h2>
            <p className="mt-3 text-slate-600 max-w-lg">Whether you want to rent, buy, list your fleet, or explore partnerships — we're one message away.</p>
            <div className="mt-8 space-y-4">
              <a href={`mailto:${BRAND.contactEmail}`} className="flex items-center gap-3 group" data-testid="home-contact-email">
                <div className="w-11 h-11 rounded-md bg-ehi-primary/10 text-ehi-primary grid place-items-center group-hover:bg-ehi-primary group-hover:text-white transition-colors"><Mail className="w-5 h-5" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Email</div>
                  <div className="font-semibold text-slate-900">{BRAND.contactEmail}</div>
                </div>
              </a>
              <a href={`tel:+${BRAND.whatsappNumber}`} className="flex items-center gap-3 group" data-testid="home-contact-phone">
                <div className="w-11 h-11 rounded-md bg-ehi-primary/10 text-ehi-primary grid place-items-center group-hover:bg-ehi-primary group-hover:text-white transition-colors"><Phone className="w-5 h-5" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Phone</div>
                  <div className="font-semibold text-slate-900">{BRAND.whatsappDisplay}</div>
                </div>
              </a>
              <a href={`https://wa.me/${BRAND.whatsappNumber}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 group" data-testid="home-contact-whatsapp">
                <div className="w-11 h-11 rounded-md bg-emerald-100 text-emerald-700 grid place-items-center group-hover:bg-emerald-600 group-hover:text-white transition-colors"><MessageCircle className="w-5 h-5" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">WhatsApp</div>
                  <div className="font-semibold text-slate-900">{BRAND.whatsappDisplay}</div>
                </div>
              </a>
            </div>
          </div>
          <div className="ehi-card p-6 md:p-8">
            <h3 className="font-display font-bold text-lg text-slate-900">Send us a quick message</h3>
            <p className="text-sm text-slate-600 mt-1">Goes straight to {BRAND.contactEmail}.</p>
            <HomeContactMiniForm />
          </div>
        </div>
      </section>
    </div>
  );
}
